import { ButtonVariants } from '@tcc-online/common-components';
import { Button } from '@tcc-online/common-components';
import { FormInput, FormSelect, FormField } from '@tcc-online/input-components';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'react-translate.ts';
import { ObjectSchema, StringSchema } from 'yup';

import {
    TRANSLATIONS_REGISTER_KEY,
    TRANSLATIONS_REQUIRED_FIELD_KEY,
} from '../../../constants/translation.constants';
import IRegistration from '../../../types/auth/IRegistration';
import { AppDispatch } from '../../../reducers/store';
import { mapParagraphs } from '../../../helpers/mapParagraphs';
import RegistrationSlice from '../../../common/slices/RegistrationSlice';

const NAME_FIELD = 'firstName';
const SURNAME_FIELD = 'lastName';
const EMAIL_FIELD = 'email';
const CATEGORY_FIELD = 'category';
const IDENTIFIER_FIELD = 'userIdentifier';

const FIRST_NAME_KEY = 'first-name';
const LAST_NAME_KEY = 'last-name';
const EMAIL_KEY = 'email';
const CATEGORY_KEY = 'category';
const IDENTIFIER_KEY = 'user-identifier';
type Values = {
    [NAME_FIELD]: string;
    [SURNAME_FIELD]: string;
    [EMAIL_FIELD]: string;
    [CATEGORY_FIELD]: string;
    [IDENTIFIER_FIELD]: string;
};

const initialValues: Values = {
    firstName: '',
    lastName: '',
    email: '',
    category: '',
    userIdentifier: '',
};

interface RegistrationFormProps extends IRegistration {
    params?: string;
}

export default function RegistrationForm(props: RegistrationFormProps) {
    const {
        id,
        description,
        displayPersonalData,
        mandatoryPersonalData,
        restrictRegistration,
        enableUserIdentifier,
        categories,
        params,
    } = props;
    const dispatch = useDispatch<AppDispatch>();
    const translate = useTranslate();

    return (
        <>
            {mapParagraphs(description)}
            <Formik
                initialValues={initialValues}
                onSubmit={submit}
                validationSchema={createValidationSchema(
                    displayPersonalData,
                    mandatoryPersonalData,
                    enableUserIdentifier,
                    categories?.length > 0,
                )}
            >
                <InnerForm {...props} />
            </Formik>
        </>
    );

    function submit(values: Values) {
        if (id) {
            if (displayPersonalData) {
                dispatch(
                    RegistrationSlice.actions.postRegistration({
                        id,
                        restrictRegistration,
                        params,
                        ...values,
                    }),
                );
            } else {
                dispatch(
                    RegistrationSlice.actions.postRegistration({
                        id,
                        restrictRegistration,
                        params,
                    }),
                );
            }
        }
    }

    function createValidationSchema(
        display: boolean,
        mandatory: boolean,
        enableIdentifier: boolean,
        enableCategories: boolean,
    ) {
        const requiredText = (key: string) =>
            translate(TRANSLATIONS_REQUIRED_FIELD_KEY, {
                0: translate(`${TRANSLATIONS_REGISTER_KEY}.${key}`),
            });
        if (mandatory) {
            const schema = {
                [NAME_FIELD]: new StringSchema().required(
                    requiredText(FIRST_NAME_KEY),
                ),
                [SURNAME_FIELD]: new StringSchema().required(
                    requiredText(LAST_NAME_KEY),
                ),
                [EMAIL_FIELD]: new StringSchema().required(
                    requiredText(EMAIL_KEY),
                ),
            };
            if (enableCategories) {
                schema[CATEGORY_FIELD] = new StringSchema().required(
                    requiredText(CATEGORY_KEY),
                );
            }
            if (enableIdentifier) {
                schema[IDENTIFIER_FIELD] = new StringSchema().required(
                    requiredText(IDENTIFIER_KEY),
                );
            }

            return new ObjectSchema(schema);
        }

        if (display) {
            return new ObjectSchema({
                [NAME_FIELD]: new StringSchema(),
                [SURNAME_FIELD]: new StringSchema(),
                [EMAIL_FIELD]: new StringSchema(),
                [CATEGORY_FIELD]: new StringSchema(),
                [IDENTIFIER_FIELD]: new StringSchema(),
            });
        }

        return new ObjectSchema();
    }
}

function InnerForm(props: IRegistration) {
    const {
        displayPersonalData,
        categories,
        mandatoryPersonalData,
        enableUserIdentifier,
    } = props;
    const translate = useTranslate(TRANSLATIONS_REGISTER_KEY);
    return (
        <Form>
            {displayPersonalData && (
                <>
                    <FormField>
                        <FormInput
                            name={NAME_FIELD}
                            label={translate(FIRST_NAME_KEY)}
                            required={mandatoryPersonalData}
                        />
                    </FormField>
                    <FormField>
                        <FormInput
                            name={SURNAME_FIELD}
                            label={translate(LAST_NAME_KEY)}
                            required={mandatoryPersonalData}
                        />
                    </FormField>
                    <FormField>
                        <FormInput
                            name={EMAIL_FIELD}
                            label={translate(EMAIL_KEY)}
                            required={mandatoryPersonalData}
                        />
                    </FormField>
                    {categories?.length > 0 && (
                        <FormField>
                            <FormSelect<string>
                                name={CATEGORY_FIELD}
                                options={categories}
                                label={translate(CATEGORY_KEY)}
                                required={mandatoryPersonalData}
                                getOptionValue={(option) => option}
                                getOptionLabel={(option) => option}
                            />
                        </FormField>
                    )}
                    {enableUserIdentifier && (
                        <FormField>
                            <FormInput
                                name={IDENTIFIER_FIELD}
                                label={translate(IDENTIFIER_KEY)}
                                required={mandatoryPersonalData}
                            />
                        </FormField>
                    )}
                </>
            )}
            <Button type="submit" variant={ButtonVariants.positive}>
                {translate('generate')}
            </Button>
        </Form>
    );
}
